import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { getOAuthToken } from 'Helpers/api';
import { authStore } from 'Stores/AuthStore';

type RetryableConfig = AxiosRequestConfig & { _retry?: boolean };

const AUTH_ERROR_STATUSES = [401, 403];

let pendingToken: Promise<string | null> | null = null;
let pendingRefresh: Promise<void> | null = null;

const resolveToken = (): Promise<string | null> => {
	if (!pendingToken) {
		pendingToken = (async () => {
			await authStore.oauthTokenPromise;
			return getOAuthToken();
		})().finally(() => {
			pendingToken = null;
		});
	}
	return pendingToken;
};

const refreshToken = (): Promise<void> => {
	if (!pendingRefresh) {
		pendingRefresh = authStore.fetchOAuthToken().finally(() => {
			pendingRefresh = null;
		});
	}
	return pendingRefresh;
};

const createTokenVerifiedClient = (baseURL: string): AxiosInstance => {
	const client = axios.create({ baseURL });

	client.interceptors.request.use(async (config: AxiosRequestConfig) => {
		const token = await resolveToken();
		return {
			...config,
			headers: {
				...config.headers,
				'Content-Type': 'application/json',
				Authorization: `Bearer ${token ?? ''}`
			}
		};
	});

	client.interceptors.response.use(
		(res) => res,
		async (error: AxiosError) => {
			const config = error.config as RetryableConfig | undefined;
			const status = error.response?.status;
			if (
				!config ||
				config._retry ||
				!status ||
				!AUTH_ERROR_STATUSES.includes(status)
			) {
				return Promise.reject(error);
			}
			config._retry = true;
			try {
				const latestToken = await resolveToken();
				if (
					config.headers?.Authorization ===
					`Bearer ${latestToken ?? ''}`
				) {
					await refreshToken();
				}
				return client(config);
			} catch {
				return Promise.reject(error);
			}
		}
	);

	return client;
};

export const lmsAxiosClient = createTokenVerifiedClient(
	process.env.REACT_APP_LMS_API_URL as string
);

export const lmsDirectoryAxiosClient = createTokenVerifiedClient(
	process.env.REACT_APP_API_URL_DIRECTORY as string
);
